import { CanonicalPresentationModelV1Schema, } from "../contracts/canonical-presentation.js";
import { stableStringify } from "../hashing/canonical-json.js";
import { isStaleActiveGenerationRun } from "../studio/derive-studio-project-lifecycle.js";
import { formatClientSafeRevisedPreviewRef } from "./derive-change-request-presentation.js";
/** Exact Slice B named outcomes (ENR-UX-009 / Master Plan Slice B). */
export const BUILD_QA_PREVIEW_OUTCOMES = [
    "permission_denied",
    "source_unknown",
    "ready_to_start_build",
    "build_queued",
    "build_in_progress",
    "build_delayed",
    "build_retry_in_progress",
    "build_failed",
    "build_stopped",
    "quality_gate_needs_attention",
    "quality_gate_passed",
    "exact_preview_ready",
    "preview_stale_or_replaced",
];
const PROHIBITED_CLIENT_TERMS = [
    "runId",
    "artifactSetHash",
    "exportBundleHash",
    "businessGraphRevisionId",
    "Quality Gate",
    "worker",
    "tenant",
    "schema",
    "GenerationRun",
    "PENDING",
    "GENERATING",
    "QA_FAILED",
    "QA_PASSED",
    "FAILED",
    "CANCELLED",
    "RETRIED",
    "SUPERSEDED",
];
const ACTIVE_BUILD_STATUSES = new Set([
    "GENERATING",
    "GENERATED",
    "QA_QUEUED",
    "QA_RUNNING",
]);
const PREVIEW_READY_RUN_STATUSES = new Set([
    "QA_PASSED",
    "QA_PASSED_WITH_WARNINGS",
    "EXPORT_READY",
    "EXPORTING",
    "EXPORTED",
]);
function baseModel(partial) {
    return CanonicalPresentationModelV1Schema.parse({
        schemaId: "enrein.canonicalPresentation.v1",
        ...partial,
    });
}
export function clientBuildQaContainsProhibitedTerminology(model) {
    const haystack = [
        model.title,
        model.reason,
        model.waitingOn ?? "",
        model.expectedResult ?? "",
        model.recovery ?? "",
        model.help ?? "",
        ...model.blockers,
        ...model.relevantRecords,
        model.primaryAction?.label ?? "",
        model.secondaryAction?.label ?? "",
    ].join("\n");
    return PROHIBITED_CLIENT_TERMS.some((term) => haystack.includes(term));
}
export function canonicalizeBuildQaPreview(model) {
    return stableStringify(model);
}
function resolveOutcome(input) {
    if (!input.projectId.trim())
        return null;
    if (input.accessDenial === "permission_denied")
        return "permission_denied";
    const run = input.latestRun;
    const status = run?.status ?? null;
    const flags = input.lifecycle?.flags;
    const stale = Boolean(flags?.staleGeneration) ||
        (run
            ? isStaleActiveGenerationRun({
                schemaId: "studio.runRow.v1",
                agencyId: "agency_presentation",
                projectId: input.projectId,
                runId: "run_presentation",
                status: run.status,
                createdAt: run.createdAt,
                updatedAt: run.updatedAt,
                recipeId: "recipe",
                qaRollup: "UNKNOWN",
                artifactSetHashPrefix: run.artifactSetHashPrefix ?? "",
            })
            : false);
    if (input.presentedPreviewVersionId &&
        input.latestPreview?.previewVersionId &&
        input.presentedPreviewVersionId.length === 64 &&
        input.latestPreview.previewVersionId.length === 64 &&
        input.presentedPreviewVersionId !== input.latestPreview.previewVersionId) {
        return "preview_stale_or_replaced";
    }
    if (status === "SUPERSEDED" && input.latestPreview?.previewVersionId) {
        return "preview_stale_or_replaced";
    }
    if (input.latestPreview?.previewStatus === "READY" &&
        input.latestPreview.previewVersionId.length === 64 &&
        (!status || PREVIEW_READY_RUN_STATUSES.has(status) || status === "SUPERSEDED")) {
        if (status === "SUPERSEDED")
            return "preview_stale_or_replaced";
        return "exact_preview_ready";
    }
    if (status === "FAILED")
        return "build_failed";
    if (status === "CANCELLED")
        return "build_stopped";
    // QA rollup FAIL must not swallow terminal build FAILED/CANCELLED (checked above).
    if (status === "QA_FAILED" || input.qaRollup === "FAIL") {
        return "quality_gate_needs_attention";
    }
    if (status === "RETRIED")
        return "build_retry_in_progress";
    if (status === "PENDING")
        return "build_queued";
    if (status && ACTIVE_BUILD_STATUSES.has(status)) {
        return stale ? "build_delayed" : "build_in_progress";
    }
    if (status === "QA_PASSED" || status === "QA_PASSED_WITH_WARNINGS") {
        return "quality_gate_passed";
    }
    if (input.lifecycle?.simplePhase === "review_and_build" &&
        input.lifecycle.primaryActionId === "confirm-build" &&
        !flags?.generating &&
        !flags?.qaFailure &&
        !flags?.generationLock) {
        return "ready_to_start_build";
    }
    if (run || input.lifecycle || input.latestPreview || input.qaRollup) {
        return "source_unknown";
    }
    return null;
}
function pairFor(outcome, input) {
    const projectId = input.projectId;
    const previewId = input.latestPreview?.previewVersionId ?? null;
    const previewRef = previewId ? formatClientSafeRevisedPreviewRef(previewId) : null;
    switch (outcome) {
        case "permission_denied":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Build",
                    condition: "NEEDS_ATTENTION",
                    title: "Build access was denied",
                    reason: "A permission or tenant check blocked build presentation.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: ["Permission denied"],
                    progress: null,
                    expectedResult: null,
                    recovery: "Confirm agency project access and try again.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Website",
                    condition: "NEEDS_ATTENTION",
                    title: "Access is not available",
                    reason: "You do not have permission to view this website status.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: null,
                    recovery: "Ask your agency for help if you still need access.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "source_unknown":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Build",
                    condition: "NEEDS_ATTENTION",
                    title: "Build status is unclear",
                    reason: "Required build or preview source fields are missing or incomplete.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: ["Incomplete source truth"],
                    progress: null,
                    expectedResult: null,
                    recovery: "Refresh the project. If this continues, review the latest run record.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Website",
                    condition: "NEEDS_ATTENTION",
                    title: "Website status is temporarily unclear",
                    reason: "We could not confirm the current website preparation status.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: null,
                    recovery: "Refresh this page. Your information is safe. Contact your agency if this continues.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "ready_to_start_build":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Build",
                    condition: "READY",
                    title: "Ready to start the website build",
                    reason: "Business information is ready. Confirm facts and start the preview build.",
                    primaryAction: {
                        intent: "START_PROJECT_BUILD",
                        projectId,
                        label: "Confirm facts & build preview",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "A website preview build starts for this project.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Website",
                    condition: "WAITING",
                    title: "Your agency is preparing your website",
                    reason: "Your information is with your agency. A preview will appear when it is ready.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Your agency",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your website preview becomes available to review.",
                    recovery: null,
                    help: "No action is needed from you right now.",
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "build_queued":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Build",
                    condition: "IN_PROGRESS",
                    title: "Website build is queued",
                    reason: "The build is waiting to start. It is not complete yet.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Build service",
                    blockers: [],
                    progress: { current: 1, total: 4, label: "Queued" },
                    expectedResult: "The build begins generating the preview.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Website",
                    condition: "WAITING",
                    title: "Your website preview is being prepared",
                    reason: "Preparation has been queued. Your information is safe.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Your agency's website preparation",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your website preview becomes available to review.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "build_in_progress":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Build",
                    condition: "IN_PROGRESS",
                    title: "Website build is in progress",
                    reason: "Generation or checks are running. The preview is not ready yet.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Build service",
                    blockers: [],
                    progress: { current: 2, total: 4, label: "Building" },
                    expectedResult: "An exact preview becomes available when checks pass.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Website",
                    condition: "WAITING",
                    title: "Your website preview is being prepared",
                    reason: "Preparation is in progress. Your information is safe and nothing is complete yet.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Your agency's website preparation",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your website preview becomes available to review.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "build_delayed":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Build",
                    condition: "WAITING",
                    title: "Website build is taking longer than expected",
                    reason: "Active generation has not progressed recently. The build service may be delayed.",
                    primaryAction: {
                        intent: "RETRY_PROJECT_BUILD",
                        projectId,
                        label: "Retry build",
                    },
                    secondaryAction: { label: "Check worker and queue", kind: "explain" },
                    waitingOn: "Build service",
                    blockers: ["Stalled generation"],
                    progress: null,
                    expectedResult: "The build resumes or a fresh retry starts.",
                    recovery: "Confirm the worker is running, then retry the build.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Website",
                    condition: "WAITING",
                    title: "Preparing your website is taking longer than usual",
                    reason: "Your agency has been notified. Your information is safe.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Your agency",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your website preview becomes available to review.",
                    recovery: null,
                    help: "No action is needed from you right now.",
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "build_retry_in_progress":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Build",
                    condition: "IN_PROGRESS",
                    title: "Build retry is in progress",
                    reason: "A retry was accepted and a new build is pending. This is not a failure.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Build service",
                    blockers: [],
                    progress: { current: 1, total: 4, label: "Retrying" },
                    expectedResult: "The retried build continues through generation and checks.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Website",
                    condition: "WAITING",
                    title: "Your website preview is being prepared again",
                    reason: "Preparation was restarted. Your information is safe. Nothing is complete yet.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Your agency's website preparation",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your website preview becomes available to review.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "build_failed":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Build",
                    condition: "NEEDS_ATTENTION",
                    title: "Website build failed",
                    reason: "The latest build did not finish successfully. No preview was produced.",
                    primaryAction: {
                        intent: "RETRY_PROJECT_BUILD",
                        projectId,
                        label: "Retry build",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: ["Build failed"],
                    progress: null,
                    expectedResult: "A successful retry produces an exact preview.",
                    recovery: "Review facts, then retry the build.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Website",
                    condition: "WAITING",
                    title: "Your agency is still preparing your website",
                    reason: "Preparation needs another pass. Your information is safe.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Your agency",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your website preview becomes available to review.",
                    recovery: null,
                    help: "Contact your agency if you have questions.",
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "build_stopped":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Build",
                    condition: "NEEDS_ATTENTION",
                    title: "Website build was stopped",
                    reason: "The latest build was cancelled. It was stopped, not completed.",
                    primaryAction: {
                        intent: "START_PROJECT_BUILD",
                        projectId,
                        label: "Start a new build",
                    },
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: ["Build stopped"],
                    progress: null,
                    expectedResult: "A new build starts for this project.",
                    recovery: "Start a new build when you are ready.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Website",
                    condition: "WAITING",
                    title: "Your agency is updating website preparation",
                    reason: "Previous preparation was stopped. Your information is safe.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Your agency",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your website preview becomes available to review.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "quality_gate_needs_attention":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Build",
                    condition: "NEEDS_ATTENTION",
                    title: "Website checks need attention",
                    reason: "Required quality checks did not pass. The preview is not ready to share.",
                    primaryAction: {
                        intent: "OPEN_QUALITY_GATE_REVIEW",
                        projectId,
                        label: "Review check results",
                    },
                    secondaryAction: { label: "Retry build", kind: "route" },
                    waitingOn: null,
                    blockers: ["Checks need attention"],
                    progress: null,
                    expectedResult: "After fixes, a passing build unlocks an exact preview.",
                    recovery: "Review the failed checks, fix facts or content, then retry.",
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Website",
                    condition: "WAITING",
                    title: "Your agency is still preparing your website",
                    reason: "Additional review is needed before your preview is ready. Your information is safe.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Your agency",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your website preview becomes available to review.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "quality_gate_passed":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Build",
                    condition: "IN_PROGRESS",
                    title: "Website checks passed",
                    reason: "Checks passed. Confirm the exact preview is available to share.",
                    primaryAction: previewId
                        ? {
                            intent: "OPEN_EXACT_PREVIEW",
                            projectId,
                            previewVersionId: previewId,
                            label: "Open exact preview",
                        }
                        : null,
                    secondaryAction: null,
                    waitingOn: previewId ? null : "Preview identity",
                    blockers: previewId ? [] : ["Exact preview identity not yet available"],
                    progress: { current: 3, total: 4, label: "Checks passed" },
                    expectedResult: "The exact preview is ready to share with the client.",
                    recovery: null,
                    help: null,
                    relevantRecords: previewRef ? [previewRef] : [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Website",
                    condition: "WAITING",
                    title: "Your website preview is almost ready",
                    reason: "Preparation finished its checks. Your preview will appear next.",
                    primaryAction: null,
                    secondaryAction: null,
                    waitingOn: "Your agency",
                    blockers: [],
                    progress: null,
                    expectedResult: "Your website preview becomes available to review.",
                    recovery: null,
                    help: null,
                    relevantRecords: [],
                    advancedDetails: null,
                }),
            };
        case "exact_preview_ready":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Preview",
                    condition: "READY",
                    title: "Exact preview is ready",
                    reason: "A checked preview is ready to share with the client.",
                    primaryAction: previewId
                        ? {
                            intent: "SHARE_EXACT_PREVIEW",
                            projectId,
                            previewVersionId: previewId,
                            label: "Share exact preview",
                        }
                        : null,
                    secondaryAction: previewId
                        ? {
                            label: "Open exact preview",
                            kind: "route",
                        }
                        : null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "The client can review this exact preview version.",
                    recovery: null,
                    help: null,
                    relevantRecords: previewRef ? [previewRef] : [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Website",
                    condition: "READY",
                    title: "Your website preview is ready",
                    reason: "You can review the exact current version of your website.",
                    primaryAction: previewId
                        ? {
                            intent: "OPEN_CLIENT_EXACT_PREVIEW",
                            previewVersionId: previewId,
                            label: "Review your website",
                        }
                        : null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "You review this exact version and can request changes or approve.",
                    recovery: null,
                    help: null,
                    relevantRecords: previewRef ? [previewRef] : [],
                    advancedDetails: null,
                }),
            };
        case "preview_stale_or_replaced":
            return {
                outcome,
                agency: baseModel({
                    role: "AGENCY",
                    journeyStage: "Preview",
                    condition: "NEEDS_ATTENTION",
                    title: "Preview was replaced",
                    reason: "The displayed preview is no longer the current exact version.",
                    primaryAction: previewId
                        ? {
                            intent: "OPEN_EXACT_PREVIEW",
                            projectId,
                            previewVersionId: previewId,
                            label: "Open current exact preview",
                        }
                        : null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: ["Stale preview"],
                    progress: null,
                    expectedResult: "Agency and client review the current exact preview.",
                    recovery: "Open and share the current exact preview version.",
                    help: null,
                    relevantRecords: previewRef ? [previewRef] : [],
                    advancedDetails: null,
                }),
                client: baseModel({
                    role: "CLIENT",
                    journeyStage: "Website",
                    condition: "NEEDS_ATTENTION",
                    title: "A newer website preview is available",
                    reason: "The version you were viewing was replaced. Review the current version.",
                    primaryAction: previewId
                        ? {
                            intent: "OPEN_CLIENT_EXACT_PREVIEW",
                            previewVersionId: previewId,
                            label: "Review current preview",
                        }
                        : null,
                    secondaryAction: null,
                    waitingOn: null,
                    blockers: [],
                    progress: null,
                    expectedResult: "You review the current exact version.",
                    recovery: null,
                    help: null,
                    relevantRecords: previewRef ? [previewRef] : [],
                    advancedDetails: null,
                }),
            };
    }
}
/**
 * Paired Agency/Client presentation for build / QA / exact preview.
 * Adapters derive presentation only — never invent run, QA, or artifact truth.
 */
export function derivePairedBuildQaPreview(input) {
    const outcome = resolveOutcome(input);
    if (!outcome)
        return null;
    const paired = pairFor(outcome, input);
    if (clientBuildQaContainsProhibitedTerminology(paired.client)) {
        return null;
    }
    return paired;
}
//# sourceMappingURL=derive-build-qa-preview.js.map